import type { ListBuilderColumn } from '../../types';

/**
 * Shared column definitions for client lists
 */

/**
 * Client name column with sales status dot, linked name, and address info
 */
export const createClientNameColumn = (): ListBuilderColumn => ({
	id: 'client-name',
	fields: [
		{
			id: 'field_1751488137880',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'salesStatus',
					objectTable: 'client',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
				valueMapping: [
					{
						color: { themeColor: 'success' },
						value: '1',
					},
					{
						color: { themeColor: 'danger' },
						value: '0',
					},
				],
			},
			displayStyle: 'dot',
			floatingLeft: true,
		},
		{
			id: 'name-field',
			link: {
				type: 'object',
				enabled: true,
				objectTable: 'client',
			},
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'name',
					objectTable: 'client',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'text',
		},
		{
			id: 'line-break-1',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'line_break',
		},
		{
			id: 'address1',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'address1',
					objectTable: 'client',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'text',
		},
		{
			id: 'field_1750195778831',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'line_break',
		},
		{
			id: 'field_1751399430207',
			dataConfig: {
				type: 'template',
				template: '{client.city}, {client.state} {client.zip}',
				templateFields: [],
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'text',
		},
	],
	headers: [
		{
			id: 'h1',
			text: 'Name',
		},
	],
	minWidth: 180,
	sortable: true,
	alignment: 'left',
	sortFields: [
		{
			direction: 'ASC',
			fieldName: 'name',
			objectTable: 'client',
		},
	],
	widthConfig: {
		mode: 'flexible',
		preset: 'large',
	},
	verticalAlignment: 'top',
});

/**
 * Location column with lead/customer badge and business type
 */
export const createLocationColumn = (): ListBuilderColumn => ({
	id: 'location',
	fields: [
		{
			id: 'field-client-id',
			displayStyle: 'badge',
			dataConfig: {
				type: 'template',
				template:
					'{leadOrCustomer:Customer=C,Lead=L}#{externalId2|externalId|id}',
				templateFields: [
					{ fieldName: 'leadOrCustomer' },
					{ fieldName: 'externalId2' },
					{ fieldName: 'externalId' },
					{ fieldName: 'id' },
				],
			},
			styleConfig: {
				size: 'xs',
				colorRule: {
					type: 'conditional',
					rules: [
						{
							field: {
								fieldName: 'leadOrCustomer',
							},
							operator: 'equals',
							value: 'Customer',
							ignoreCase: true,
							color: { type: 'info' },
						},
						{
							field: {
								fieldName: 'leadOrCustomer',
							},
							operator: 'equals',
							value: 'Lead',
							ignoreCase: true,
							color: { type: 'warning' },
						},
					],
				},
				variant: 'solid',
			},
		},
		{
			id: 'field_1750394549312',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'line_break',
		},
		{
			id: 'field_1750361746643',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'businessType',
					objectTable: 'client',
				},
			},
			styleConfig: {
				size: 'xs',
				colorRule: {
					type: 'static',
					staticColor: { type: 'neutral' },
				},
			},
			displayStyle: 'badge',
		},
	],
	headers: [
		{
			id: 'h3-lead-cust',
			text: 'Lead / Cust',
		},
		{
			id: 'h3-line-break',
			text: '',
		},
		{
			id: 'h3-business-type',
			text: 'Business Type',
		},
	],
	sortable: true,
	alignment: 'left',
	sortFields: [
		{
			direction: 'ASC',
			fieldName: 'leadOrCustomer',
			objectTable: 'client',
		},
		{
			direction: 'ASC',
			fieldName: 'name',
			objectTable: 'client',
		},
	],
	verticalAlignment: 'top',
});

/**
 * Lifecycle stage column with custom selector component
 */
export const createLifecycleStageColumn = (): ListBuilderColumn => ({
	id: 'lifecycle-stage',
	fields: [
		{
			id: 'lifecycle-stage-name',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'lifecycleStageName',
					objectTable: 'client',
				},
			},
			displayStyle: 'custom',
			customComponent: {
				id: 'client-lifecycle-stage-selector',
			},
		},
	],
	headers: [
		{
			id: 'lifecycle-stage-header',
			text: 'Stage',
		},
	],
	sortable: true,
	alignment: 'center',
	sortFields: [
		{
			direction: 'ASC',
			fieldName: 'fkClientLifecycleStageId',
			objectTable: 'client',
			nullsLast: true,
		},
	],
	widthConfig: {
		mode: 'flexible',
		preset: 'small',
	},
	verticalAlignment: 'top',
});

/**
 * Salespeople column with primary and alternate salesperson
 */
export const createSalespeopleColumn = (): ListBuilderColumn => ({
	id: 'salespeople',
	fields: [
		{
			id: 'salesperson-name',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'salespersonName',
					objectTable: 'client',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'text',
		},
		{
			id: 'line-break-1',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'line_break',
		},
		{
			id: 'alternate-salesperson-name',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'alternateSalespersonName',
					objectTable: 'client',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'text',
		},
	],
	headers: [
		{
			id: 'salesperson-header',
			text: 'Salesperson',
		},
		{
			id: 'header-line-break',
			text: '',
		},
		{
			id: 'alternate-salesperson-header',
			text: 'Alternate',
		},
	],
	sortable: false,
	alignment: 'left',
	widthConfig: {
		mode: 'flexible',
		preset: 'medium',
	},
	verticalAlignment: 'top',
});

/**
 * Contact info column with phone and email
 */
export const createContactInfoColumn = (): ListBuilderColumn => ({
	id: 'contact-info',
	fields: [
		{
			id: 'phone',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'phone',
					objectTable: 'client',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'phone',
		},
		{
			id: 'line-break',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'line_break',
		},
		{
			id: 'email',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'email',
					objectTable: 'client',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'email',
		},
	],
	headers: [
		{
			id: 'h2-phone',
			text: 'Phone',
		},
		{
			id: 'h2-line-break',
			text: '',
		},
		{
			id: 'h2-email',
			text: 'Email',
		},
	],
	sortable: false,
	alignment: 'left',
	verticalAlignment: 'top',
});

/**
 * Reorder column with average invoice revenue and forecasted reorder date
 */
export const createReorderColumn = (): ListBuilderColumn => ({
	id: 'reorder',
	fields: [
		{
			id: 'field_1750475570885',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'forecast',
					objectTable: 'client',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'currency',
		},
		{
			id: 'field_1750475618703',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'line_break',
		},
		{
			id: 'field_1750475452349',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'forecastedReorderDate',
					objectTable: 'client',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'relative_date',
		},
	],
	headers: [
		{
			id: 'header_1750475608556',
			text: 'Reorder Due',
		},
	],
	sortable: true,
	alignment: 'left',
	sortFields: [
		{
			direction: 'ASC',
			fieldName: 'forecastedReorderDate',
			objectTable: 'client',
			nullsLast: true,
		},
	],
	verticalAlignment: 'top',
});

/**
 * Task complete column with complete action button
 */
export const createTaskCompleteColumn = (): ListBuilderColumn => ({
	id: 'task-complete-column',
	fields: [
		{
			id: 'complete-action',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'actions_dropdown',
			dropdownActions: [
				{
					id: 'complete-task-action',
					label: 'Complete',
					iconName: 'check',
					callbackName: 'completeTask',
					variant: 'success',
					visibility: 'visible',
				},
			],
		},
	],
	headers: [
		{
			id: 'h-complete',
			text: '',
		},
	],
	width: 50,
	align: 'center',
	sortable: false,
});

/**
 * Next activity column — shows the task subject and start time.
 *
 * Note: the priority badge used to live here as a third stacked field. It now
 * lives in the dedicated Status/Priority chip column ({@link createNextActivityChipsColumn})
 * so the user can change priority inline, alongside the To-Do/Done status
 * chip. Removing it from this column also keeps the activity cell visually
 * lighter — just the subject + time.
 */
export const createNextActivityColumn = (): ListBuilderColumn => ({
	id: 'next-activity-column',
	fields: [
		{
			id: 'activity-title',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'nextActivityTitle',
					objectTable: 'client',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'text',
		},
		{
			id: 'activity-line-break',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'line_break',
		},
		{
			id: 'activity-time',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'nextActivityTime',
					objectTable: 'client',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
				formatOptions: {
					dateFormat: 'weekday-short-with-time',
					timeField: 'nextActivityTimeOfDay',
					timezoneField: 'nextActivityTimezone',
				},
			},
			displayStyle: 'date',
		},
	],
	headers: [
		{
			id: 'h-next-activity',
			text: 'Next Activity',
		},
	],
	sortable: true,
	alignment: 'left',
	sortFields: [
		{
			direction: 'ASC',
			fieldName: 'nextActivityTime',
			objectTable: 'task_data',
		},
	],
	widthConfig: {
		mode: 'flexible',
		preset: 'large',
	},
	verticalAlignment: 'top',
});

/**
 * Status + Priority chip column for the activity-based client lists
 * (Scheduled Today, Overdue). Replaces the legacy "green check" complete
 * button and the priority badge that used to sit inside the Next Activity
 * cell.
 *
 * Layout (one column, two stacked chips):
 *   [To-Do ▾]
 *   [Medium ▾]
 *
 * Both chips read from denormalized task fields on the client row
 * (`nextActivityTaskId`, `nextActivityPriority`). The custom components are
 * registered in
 *   modules/clients/src/components/list-builder/registerComponents.ts.
 */
export const createNextActivityChipsColumn = (): ListBuilderColumn => ({
	id: 'next-activity-chips-column',
	fields: [
		{
			id: 'next-activity-status-chip',
			dataConfig: {
				type: 'field',
				field: {
					// No real "status" field — server filters _isCompleted = 0, so
					// the chip is effectively always "pending" / To-Do. We still
					// reference nextActivityTaskId so the selector can read it
					// straight off the field's value if it ever needs to.
					fieldName: 'nextActivityTaskId',
					objectTable: 'client',
				},
			},
			displayStyle: 'custom',
			customComponent: { id: 'client-next-activity-status-selector' },
		},
		{
			id: 'next-activity-chips-line-break',
			dataConfig: { type: 'custom_text', customText: '' },
			displayStyle: 'line_break',
		},
		{
			id: 'next-activity-priority-chip',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'nextActivityPriority',
					objectTable: 'client',
				},
			},
			displayStyle: 'custom',
			customComponent: { id: 'client-next-activity-priority-selector' },
		},
	],
	// Two stacked header labels mirror the stacked chips below: Status sits
	// over the To-Do/Done chip and Priority sits over the Low/Medium/High chip.
	headers: [
		{ id: 'h-next-activity-status', text: 'Status' },
		{ id: 'h-next-activity-priority', text: 'Priority' },
	],
	width: 110,
	align: 'center',
	sortable: false,
});

/**
 * Client actions column with quickView, edit, and recover actions
 */
export const createClientActionsColumn = (): ListBuilderColumn => ({
	id: 'actions-column',
	fields: [
		{
			id: 'actions-dropdown',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'actions_dropdown',
			dropdownActions: [
				{
					id: 'quick-view-action',
					label: 'Quick View',
					iconName: 'eye',
					callbackName: 'quickView',
					variant: 'secondary',
					visibility: 'visible',
				},
				{
					id: 'edit-action',
					label: 'Edit',
					iconName: 'edit',
					callbackName: 'edit',
					variant: 'primary',
					visibility: 'visible',
					conditionalVisibility: {
						field: 'deletedDate',
						condition: 'is_null',
					},
				},
				{
					id: 'recover-action',
					label: 'Recover',
					iconName: 'undo',
					callbackName: 'recoverClient',
					variant: 'secondary',
					visibility: 'visible',
					conditionalVisibility: {
						field: 'deletedDate',
						condition: 'is_not_null',
					},
				},
				{
					id: 'remove-client-action',
					label: 'Remove from Group',
					iconName: 'trash',
					callbackName: 'removeClient',
					variant: 'danger',
					visibility: 'visible',
				},
			],
		},
	],
	headers: [
		{
			id: 'actions-header',
			text: '',
		},
	],
	sortable: false,
	widthConfig: {
		mode: 'fixed',
		width: 100,
	},
	verticalAlignment: 'middle',
});

/**
 * Task actions column with edit and delete task actions
 */
export const createTaskActionsColumn = (): ListBuilderColumn => ({
	id: 'actions-column',
	fields: [
		{
			id: 'actions-dropdown',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'actions_dropdown',
			dropdownActions: [
				{
					id: 'edit-task-action',
					label: 'Edit',
					iconName: 'edit',
					callbackName: 'editTask',
					variant: 'primary',
					visibility: 'visible',
				},
				{
					id: 'delete-task-action',
					label: 'Delete',
					iconName: 'trash',
					callbackName: 'deleteTask',
					variant: 'danger',
					visibility: 'visible',
				},
			],
		},
	],
	headers: [
		{
			id: 'h-actions',
			text: '',
		},
	],
	width: 100,
	align: 'center',
	sortable: false,
});

/**
 * Proximity column for near-me list
 */
export const createProximityColumn = (): ListBuilderColumn => ({
	id: 'proximity',
	fields: [
		{
			id: 'distance-field',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'proximity',
					objectTable: 'client',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
				formatOptions: {
					suffix: ' mi',
					decimalPlaces: 1,
				},
			},
			displayStyle: 'number',
		},
	],
	headers: [
		{
			id: 'h-proximity',
			text: 'Distance',
		},
	],
	sortable: true,
	alignment: 'right',
	// PD-13545 — match the backend's computed alias `proximity` everywhere it's
	// referenced. clients.service.ts adds the haversine distance to the SELECT as
	// `… AS proximity` and excludes `client.proximity` from the field list. The
	// list-builder emits the sort fieldName verbatim into ORDER BY, and the display
	// field + the list's requiredFields into the SELECT — so all three must say
	// `proximity`. `distanceFromCenter` is neither a real column nor an alias, so
	// it produced `ORDER BY client.distanceFromCenter` (Chris's first fix) AND
	// leaked into the SELECT field list (this fix) → "Unknown column ...".
	sortFields: [
		{
			direction: 'ASC',
			fieldName: 'proximity',
			objectTable: 'client',
		},
	],
	widthConfig: {
		mode: 'fixed',
		width: 100,
	},
	verticalAlignment: 'middle',
});

/**
 * Standard column set for most client lists
 * Includes: client-name, location, lifecycle-stage, salespeople, contact-info, reorder, actions
 */
export const createStandardClientColumns = (): Array<ListBuilderColumn> => [
	createClientNameColumn(),
	createLocationColumn(),
	createLifecycleStageColumn(),
	createSalespeopleColumn(),
	createContactInfoColumn(),
	createReorderColumn(),
	createClientActionsColumn(),
];

/**
 * Column set without lifecycle stage
 * Includes: client-name, location, salespeople, contact-info, reorder, actions
 */
export const createClientColumnsNoLifecycle = (): Array<ListBuilderColumn> => [
	createClientNameColumn(),
	createLocationColumn(),
	createSalespeopleColumn(),
	createContactInfoColumn(),
	createReorderColumn(),
	createClientActionsColumn(),
];

/**
 * Column set for activity-based lists (scheduled today, overdue).
 *
 * Order: client name, status/priority chips, location, salespeople, contact
 * info, next activity (subject + time), task actions.
 *
 * The Status + Priority chip stack (see {@link createNextActivityChipsColumn})
 * sits in the second column. It replaces the legacy green-check complete
 * column; the priority badge was also pulled out of the Next Activity cell so
 * it can be edited inline alongside the status chip.
 *
 * Reorder Due is intentionally omitted on these lists — these tabs are about
 * scheduled / overdue activity, not buying cadence, so the reorder column
 * just adds noise.
 */
export const createActivityListColumns = (): Array<ListBuilderColumn> => [
	createClientNameColumn(),
	createNextActivityChipsColumn(),
	createLocationColumn(),
	createSalespeopleColumn(),
	createContactInfoColumn(),
	createNextActivityColumn(),
	createTaskActionsColumn(),
];

/**
 * Column set for near-me list
 * Includes: client-name, location, salespeople, contact-info, reorder, proximity, actions
 */
export const createNearMeColumns = (): Array<ListBuilderColumn> => [
	createClientNameColumn(),
	createLocationColumn(),
	createSalespeopleColumn(),
	createContactInfoColumn(),
	createReorderColumn(),
	createProximityColumn(),
	createClientActionsColumn(),
];
